import { Link } from 'react-router-dom'
import { ChevronRight, Home } from 'lucide-react'

export interface BreadcrumbItem {
  name: string
  url: string
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[]
  className?: string
}

/**
 * Visual breadcrumb navigation component
 * Schema markup is handled by useSEO hook - pass the same items to both
 */
export function Breadcrumbs({ items, className = '' }: BreadcrumbsProps) {
  if (items.length === 0) return null

  return (
    <nav aria-label="Breadcrumb" className={`text-muted-foreground text-sm ${className}`}>
      <ol className="flex flex-wrap items-center gap-1.5">
        {/* Home is always first */}
        <li className="flex items-center">
          <Link to="/" className="hover:text-foreground flex items-center gap-1 transition-colors" aria-label="Home">
            <Home className="h-4 w-4" />
          </Link>
        </li>

        {items.map((item, index) => {
          const isLast = index === items.length - 1

          return (
            <li key={item.url} className="flex items-center gap-1.5">
              <ChevronRight className="text-muted-foreground/50 h-4 w-4" aria-hidden="true" />
              {isLast ? (
                <span className="text-foreground font-medium" aria-current="page">
                  {item.name}
                </span>
              ) : (
                <Link to={item.url} className="hover:text-foreground transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

/**
 * Helper to generate breadcrumb items from a path
 * Example: '/services/websites' => [{ name: 'Services', url: '/services' }, { name: 'Websites', url: '/services/websites' }]
 */
// eslint-disable-next-line react-refresh/only-export-components -- helper co-located with Breadcrumbs; 24 importers, moving would churn; dev-only Fast Refresh hint
export function generateBreadcrumbs(pathname: string, labels?: Record<string, string>): BreadcrumbItem[] {
  const segments = pathname.split('/').filter(Boolean)
  const items: BreadcrumbItem[] = []

  let currentPath = ''
  for (const segment of segments) {
    currentPath += `/${segment}`
    const name = labels?.[currentPath] || labels?.[segment] || formatSegment(segment)
    items.push({ name, url: currentPath })
  }

  return items
}

/**
 * Format a URL segment into a readable name
 * Example: 'festival-websites' => 'Festival Websites'
 */
function formatSegment(segment: string): string {
  return segment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Common breadcrumb label mappings for the site
 */
// eslint-disable-next-line react-refresh/only-export-components -- label map co-located with Breadcrumbs; dev-only Fast Refresh hint
export const breadcrumbLabels: Record<string, string> = {
  '/services': 'Services',
  '/portfolio': 'Portfolio',
  '/blog': 'Blog',
  '/platform': 'Platform',
  '/platform/features': 'Features',
  '/resources': 'Resources',
  '/packages': 'Packages',
  '/about': 'About',
  '/contact': 'Contact',
  '/get-started': 'Get Started',
  '/book': 'Book a Discovery Call',
  '/checklist': 'Website Checklist',
  '/requests': 'Request an Update',
  '/who-we-help': 'Who We Help',
  '/compare': 'Compare',
  // Who we serve segments
  'music-festivals': 'Music Festivals',
  'choral-festivals': 'Choral Festivals & Choruses',
  'young-artist-programs': 'Young Artist & Conducting Programs',
  'pre-college-summer-programs': 'Pre-College & Summer Programs',
  'churches-sacred-music': 'Churches & Sacred Music',
  'music-schools': 'Music Schools & Studios',
  // Compare pages
  acceptd: 'vs Acceptd',
  squarespace: 'vs Squarespace',
  wix: 'vs Wix',
  wordpress: 'vs WordPress',
  // Service pages
  websites: 'Websites',
  'festival-websites': 'Festival Websites',
  'festival-retainer': 'Festival Retainer',
  'digital-systems': 'Digital Systems',
  'application-funnels': 'Application Funnels',
  'digital-partnership': 'Digital Partnership',
  // Platform features
  donations: 'Donations',
  website: 'Website',
  events: 'Events',
  email: 'Email',
  team: 'Team',
  analytics: 'Analytics',
  // Blog
  'chroma-festival-case-study': 'Chroma Festival Case Study',
  'festivals-losing-applicants': 'Why Festivals Are Losing Applicants',
  'best-website-platforms-for-music-festivals': 'Best Website Platforms for Music Festivals',
}
